/* tslint:disable:triple-equals */
import { Property } from './property.model';
import { ContributorsGroup } from './contributors-group.model';
import { Contributor } from './contributor.model';
import { DealImage } from './deal-image.model';
import { Stage } from './stage.model';
import { Country } from './country.model';
import { Milestone } from './milestone.model';
import { DealCategory } from './deal-category.model';
import { DealOperationCategoriesArray, DealOperationCategory } from '../constants/deal-operation-categories';
import { DealOperationSubcategoriesArray, DealOperationSubcategory } from '../constants/deal-operation-subcategories';
import { DealOperationRequiredFile } from './deal-operation-required-file.model';
import { DealOperationNDAFile } from './deal-operation-nda-file.model';

type StatusTypeOptions = 'active' | 'disabled' | 'archived';

export const StatusTypes = {
  ACTIVE: 'active' as StatusTypeOptions,
  DISABLED: 'disabled' as StatusTypeOptions,
  ARCHIVED: 'archived' as StatusTypeOptions
};

export enum DealType {
  Deal= 'deal',
  Portfolio = 'portfolio',
}

export interface Deal {
  id: number;
  templateId?: number;
  dealType: DealType;
  code: string;
  name: string;
  type: string;
  usage?: any;
  style?: string;
  sector?: string;
  geography?: string;
  occupancyRate?: number;
  squareFeet?: number;
  squareFeetResidential?: number;
  squareFeetOthers?: number;
  plots?: number;
  units?: number;
  buildability?: string;
  protection?: string;

  clientName?: string;

  locationAddress: string;
  locationCity: string;
  locationZipCode: string;
  locationState: string;
  locationCountryId: number;
  locationCountry?: Country;
  locationLat?: number;
  locationLng?: number;

  status: StatusTypeOptions | string;
  isDeleted: boolean;
  workflowStatus?: string;
  workflowClosedDate?: Date;
  workflowClosedYear?: string;
  workflowClosedQuarter?: string;
  currency: string;
  measuring?: string;

  yearBuilt?: string;
  yearRenoved?: string;

  isPrivate: boolean;
  isMultiple: boolean;
  bildDueDate: Date;
  closingDate: Date;
  purchasePrice: number;

  profileBuyer?: string;
  profileRecordedBuyer?: string;
  profileSeller?: string;
  profileRecordedSeller?: string;

  commercialTitle?: string;
  commercialResume?: string;
  commercialDescription?: string;
  commercialQuote?: string;
  commercialNotes?: string;

  financialAcquisition?: number;
  financialNOI?: number;
  financialDiscountRate?: number;
  financialNOIGrowthRate?: number;
  financialFinancingCost?: number;
  financialAcquisitionCost?: number;
  financialBudgetHardCost?: number;
  financialBudgetSoftCost?: number;
  financialBrokerComission?: number;
  financialClosingCost?: number;
  financialPropertyOwner?: string;
  financialListingBroker?: string;
  financialOperatingPartner?: string;
  financialNotes?: string;

  revenueCurrency?: string;
  revenueExpectedClosing?: Date;
  revenueOperationCost?: number;
  revenueFeeType?: string;
  revenueFeeValue?: number;
  revenueFeeSplitActive?: boolean;
  revenueFeeSplitValue?: number;
  revenueRiskAdjustment?: number;

  marketDescription?: string;
  marketQuote?: string;
  marketNotes?: string;

  position: number;
  bcPublicKey?: string;

  launchDealInformation?: boolean;
  launchContributors?: boolean;
  launchContacts?: boolean;
  launchDealflow?: boolean;
  launchDone?: boolean;

  operationCategory?: DealOperationCategory;
  operationSubcategory?: DealOperationSubcategory;
  operationPublishDate?: Date;
  operationCloseDate?: Date;
  operationBindingOffersEndDate?: Date;
  operationNonBindingOffersEndDate?: Date;
  operationIsBC?: boolean;
  operationRequiredFiles?: DealOperationRequiredFile[];
  operationNDAFiles?: DealOperationNDAFile[];

  isOverviewFinancialActive?: boolean;
  isOverviewRevenueActive?: boolean;
  isOverviewCommercialTeamActive?: boolean;

  dateCreated: Date;
  dateLastUpdated: Date;

  properties: Property[];
  propertiesCount?: number;
  contributorsGroups: ContributorsGroup[];
  contributors: Contributor[];
  images: DealImage[];

  categories?: DealCategory[];

  milestones?: Milestone[];

  criticalDate?: any;
  stage?: any;
  country?: string;
  progress?: number;
  user?: any;
  currentStage?: Stage;
  capRate?: number;
  priceRange?: string;
  capRateRange?: string;
  userId?: number;

  stages?: Stage[];
  stagesCount?: number;

  // Computed fields returned by the list endpoint
  totalPrice?: number;
  childPropertiesCount?: number;
  rootPropertiesCount?: number;
  typeLabel?: string;
  subtypeLabel?: string;
  imageUrl?: string;
}

export const DealUtils = {

  getDealImage(deal: Deal) {
    if (deal.images && deal.images.length > 0) {
      return deal.images[0].image.url;
    }

    if (deal.properties && deal.properties.length > 0) {
      if (deal.properties[0].images && deal.properties[0].images.length > 0) {
        return deal.properties[0].images[0].image.url;
      }
    }

    return 'assets/imgs/deals/default.svg';
  },

  getOfferStatus(deal: Deal) {
    const { operationNonBindingOffersEndDate, operationBindingOffersEndDate } = deal;
    const today = new Date().toISOString();

    if (operationNonBindingOffersEndDate) {
      if (today < `${operationNonBindingOffersEndDate}`) {
        return 'non-binding';
      }
      if (today < `${operationBindingOffersEndDate}`) {
        return 'binding';
      }
      return 'closed';
    }

    if (today < `${operationBindingOffersEndDate}`) {
      return 'binding';
    }
    return 'closed';
  },

  getGroupedTypeLabel(deal: Deal) {
    const types: string[] = [];
    deal.properties.forEach(property => {
      if (property.type) {
        const type = DealOperationCategoriesArray.find(c =>
          c.label.toLowerCase() == `${property.type}`.toLowerCase()
          || c.value.toLowerCase() == `${property.type}`.toLowerCase())?.label || `${property.type}`;
        if (!types.find(t => t.toLowerCase() == type.toLowerCase())) {
          types.push(type);
        }
      }
    });
    return types.length == 0 ? ' - ' : types.length < 3 ? types.join(' / ') : 'Multiproducto';
  },

  getGroupedSubtypeLabel(deal: Deal) {
    const subtypes: string[] = [];
    deal.properties.forEach(property => {
      if (property.subtype) {
        const subtype = DealOperationSubcategoriesArray.find(c =>
          c.label.toLowerCase() == `${property.subtype}`.toLowerCase()
          || c.value.toLowerCase() == `${property.subtype}`.toLowerCase())?.label || `${property.subtype}`;
        if (!subtypes.find(s => s.toLowerCase() == subtype.toLowerCase())) {
          subtypes.push(subtype);
        }
      }
    });
    return subtypes.length == 0 ? ' - ' : subtypes.length < 3 ? subtypes.join(' / ') : 'Multiuso';
  }
};
